import { list } from '@fitogest/reactive-form-builder/utils';
import { StorageManager } from '../../../../../../common/storage-manager.class';
import {
    InputType,
    DropdownData,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    DropdownFilterContext,
    ReactiveDropdownCatalogs,
} from '@fitogest/reactive-form-builder/interfaces';
import { ReactiveSelectionResolver } from '@fitogest/reactive-form-builder/utils';
import { environment } from '../../../../../../../environments/environment';
import { ClienteModel } from '../../../../../../models/cliente.model';
import { FincasModel } from '../../../../../../models/form-common/fincas.module';
import { ControlSanitarioModel } from '../../../../../../models/tareas/control-sanitario.model';

export class ControlSanitarioFormDefinition {
    public readonly hasClients = environment.features.hasClients;
    public readonly fincaName = environment.features.fincaName;

    public formType!: FormType;
    public model: Partial<ControlSanitarioModel> = {};
    public formFields!: Form<ControlSanitarioModel>;
    public fieldNames: FormField<ControlSanitarioModel>[] = [];
    public fieldLabels: string[] = [];

    public fincas: DropdownData<FincasModel> = list();
    public clientes: DropdownData<ClienteModel> = list();
    public tiposTratamientos: DropdownData<string> = list(
        ['...', null],
        'ENFERMEDADES INFECCIOSAS',
        'INMUNIZACIÓN',
        'VACUNACIÓN',
        'DESPARASITACIÓN',
    );

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();

    constructor(type: FormType, model: Partial<ControlSanitarioModel> = {}) {
        this.formType = +type;
        this.model = model;

        this.formFields = {
            name: 'control-sanitario',
            header: 'Datos del control sanitario',
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
            dropdownCatalogs: this.getDropdownCatalogs(),
        };
    }

    private getDropdownCatalogs(): ReactiveDropdownCatalogs {
        return {
            critical: ['id_finca'],
            deferred: ['id_cliente'],
            lazy: [],
        };
    }

    private getFormSections(): FormSection<ControlSanitarioModel>[] {
        const sections: FormSection<ControlSanitarioModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getMainFields(),
                order: 1,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getTreatmentFields(),
                order: 2,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 3,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getMainFields(): FormField<ControlSanitarioModel>[] {
        const fincaFieldName = this.formType < FormType.DELETION ? 'id_finca' : 'finca';
        const fincaInputType = this.formType < FormType.DELETION ? InputType.DROPDOWN_SEARCH : InputType.TEXT;

        const fields: FormField<ControlSanitarioModel>[] = [
            {
                name: fincaFieldName,
                dependencies: [
                    {
                        source: 'id_cliente',
                        target: fincaFieldName,
                        kind: 'filter'
                    }
                ],
                label: this.fincaName,
                input: {
                    type: fincaInputType,
                    data: this.fincas,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it, context): boolean => this.filterFincaByClient(it.value as FincasModel | null, context),
                    },
                },
                width: 3,
                conditions: this.conditions({ isRequired: true }),
                order: 1,
            },
            {
                name: 'fecha_inicio',
                label: 'Fecha inicio',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'fecha_fin',
                label: 'Fecha fin',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 3,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'fecha_plazo',
                label: 'Plazo de seguridad',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getTreatmentFields(): FormField<ControlSanitarioModel>[] {
        const fields: FormField<ControlSanitarioModel>[] = [
            {
                name: 'tipo_tratamiento',
                label: 'Tipo de tratamiento',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.tiposTratamientos,
                    isEditable: true,
                },
                width: 3,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'veterinario',
                label: 'Veterinario',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'diagnostico',
                label: 'Diagnóstico',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 3,
            },
            {
                name: 'producto',
                label: 'Producto',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 4,
            },
            {
                name: 'animales_tratados',
                label: 'Animales tratados',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: this.conditions(),
                order: 5,
            },
            {
                name: 'observaciones',
                label: 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: true,
                    textAreaConfiguration: {
                        hasVoiceTranscription: true,
                        canTakePhoto: false,
                        photoField: undefined,
                    },
                },
                width: 12,
                conditions: this.conditions(),
                order: 6,
            },
        ];

        return this.registerVisibleFields(fields);
    }

    private getHiddenFields(): FormField<ControlSanitarioModel>[] {
        return [
            {
                name: 'id',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id ?? null,
                    isEditable: false,
                },
                width: 2,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'id_cliente',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.resolveInitialClientId(),
                    isEditable: false,
                },
                computed: {
                    dependsOn: ['id_finca'],
                    resolve: (context) => this.resolveSelectedClientId(context.getValue('id_finca')),
                },
                width: 2,
                conditions: this.conditions({ isVisible: this.hasClients }),
                order: 2,
            },
            {
                name: 'id_usuario',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.resolveUserId(),
                    isEditable: false,
                },
                computed: {
                    resolve: () => this.resolveUserId(),
                },
                width: 2,
                conditions: this.conditions(),
                order: 3,
            },
        ];
    }

    private registerVisibleFields(fields: FormField<ControlSanitarioModel>[]): FormField<ControlSanitarioModel>[] {
        const visibleFields = fields.filter((field) => field.conditions.isVisible);
        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private filterFincaByClient(finca: FincasModel | null, context?: DropdownFilterContext<ControlSanitarioModel>): boolean {
        if (finca === null || !this.hasClients || this.formType === FormType.DUPLICATION) {
            return true;
        }

        const selectedClientId = this.resolveClientId(context?.model.get('id_cliente')?.value ?? this.model.id_cliente) || this.client?.id;

        if (!selectedClientId) {
            return true;
        }

        return finca.id_cliente?.toString() === selectedClientId.toString();
    }

    private resolveInitialClientId(): string | null {
        return this.resolveClientId(this.model.id_cliente) || this.client?.id || null;
    }

    private resolveSelectedClientId(selectedFincaValue: unknown = this.model.id_finca): string {
        const selectedFinca = this.resolveSelectedDropdownEntity(this.fincas, selectedFincaValue);

        if (selectedFinca?.id_cliente !== undefined && selectedFinca.id_cliente !== null) {
            return String(selectedFinca.id_cliente);
        }

        return this.resolveInitialClientId() ?? '';
    }

    private resolveClientId(value: unknown): string {
        const id = ReactiveSelectionResolver.extractId(value);

        return id === null ? '' : String(id);
    }

    private resolveSelectedDropdownEntity<TEntity extends object>(
        dropdownData: DropdownData<TEntity>,
        selectedValue: unknown,
    ): TEntity | null {
        return ReactiveSelectionResolver.resolveSelectedEntities(dropdownData, selectedValue)[0] ?? null;
    }

    private resolveUserId(): string {
        return String(this.model.id_usuario ?? this.user?.id ?? '');
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }
}
